// src/chatbot/services/chatbotApiService.ts
import type {
	ChatbotApiRequest,
	ChatbotApiResponse,
	ChatbotApiSuccessResponse,
	ChatbotCartSnapshot,
	ChatbotCartItem,
	ChatbotCartOptionGroup,
	ChatbotCartSelection,
	ParsedChatbotResponse,
	ParsedOrderItem,
	ParsedVariant,
	ParsedTagValue,
	StreamCompletionResult,
	StreamData
} from '../types/chatbot';
const chatApiUrl = import.meta.env.VITE_CHATAPI_URL;

const TAG_PATTERN = /<([A-Z0-9_-]+)>([\s\S]*?)<\/\1>/gi;

const normalizeContentTypeForApi = (type?: string): 'wav' | 'ogg' | null => {
	if (!type) {
		return null;
	}
	const lower = type.toLowerCase();
	if (lower.includes('wav')) {
		return 'wav';
	}
	if (lower.includes('ogg')) {
		return 'ogg';
	}
	return null;
};

const normalizeQuotes = (value: string): string =>
	value
		.replace(/[\u2018\u2019]/g, "'")
		.replace(/[\u201C\u201D]/g, '"')
		.replace(/\btrue\b/gi, 'true')
		.replace(/\bfalse\b/gi, 'false')
		.replace(/\bnone\b/gi, 'null');

const parseTagValue = (rawValue: string): ParsedTagValue => {
	const trimmed = rawValue.trim();
	if (!trimmed) {
		return '';
	}

	const normalized = normalizeQuotes(trimmed);
	const isJsonLike =
		(normalized.startsWith('[') && normalized.endsWith(']')) ||
		(normalized.startsWith('{') && normalized.endsWith('}'));

	if (isJsonLike) {
		try {
			return JSON.parse(normalized) as ParsedTagValue;
		} catch {
			try {
				return JSON.parse(normalized.replace(/'/g, '"')) as ParsedTagValue;
			} catch {
				// Fallback to string below
			}
		}
	}

	if (normalized.toLowerCase() === 'null') {
		return null;
	}

	return normalized;
};

const extractTagsFromContent = (
	content: string
): { text: string; tags: Record<string, ParsedTagValue> } => {
	let cleanedText = '';
	let lastIndex = 0;
	const tags: Record<string, ParsedTagValue> = {};

	TAG_PATTERN.lastIndex = 0;
	let match: RegExpExecArray | null;
	while ((match = TAG_PATTERN.exec(content)) !== null) {
		cleanedText += content.slice(lastIndex, match.index);
		const tagName = match[1]?.toLowerCase();
		const tagValue = match[2] ?? '';
		if (tagName) {
			tags[tagName] = parseTagValue(tagValue);
			// Debug cart tag extraction
			if (tagName === 'cart') {
				console.log('[chatbotApiService] Extracted cart tag:', { tagName, tagValue, parsed: tags[tagName] });
			}
		}
		lastIndex = match.index + match[0].length;
	}

	cleanedText += content.slice(lastIndex);

	return { text: cleanedText, tags };
};

const removeTrailingPartialTag = (text: string): string => {
	const lastOpen = text.lastIndexOf('<');
	const lastClose = text.lastIndexOf('>');
	if (lastOpen > lastClose) {
		return text.slice(0, lastOpen);
	}
	return text;
};

const ensureStringArray = (value: ParsedTagValue | undefined): string[] => {
	if (Array.isArray(value)) {
		return value
			.map(entry => {
				if (typeof entry === 'string') {
					return entry.trim();
				}
				if (entry === null || entry === undefined) {
					return '';
				}
				return String(entry).trim();
			})
			.filter(Boolean);
	}

	if (typeof value === 'string') {
		const normalized = normalizeQuotes(value.trim());
		if (!normalized) {
			return [];
		}

		if (
			(normalized.startsWith('[') && normalized.endsWith(']')) ||
			(normalized.startsWith('{') && normalized.endsWith('}'))
		) {
			const parsed = parseTagValue(normalized);
			if (parsed && typeof parsed !== 'string') {
				return ensureStringArray(parsed);
			}
		}

		const trimmedValue = normalized.replace(/^\[|\]$/g, '');
		return trimmedValue
			.split(',')
			.map(part => part.trim().replace(/^['"]|['"]$/g, ''))
			.filter(Boolean);
	}

	return [];
};

const mergeUniqueStrings = (...groups: Array<string[]>): string[] => {
	const unique = new Set<string>();
	for (const group of groups) {
		for (const entry of group) {
			const normalized = entry?.trim?.() ?? String(entry).trim();
			if (normalized) {
				unique.add(normalized);
			}
		}
	}
	return Array.from(unique);
};

const ensureOrderItemDetails = (value: ParsedTagValue | unknown): ParsedOrderItem[] => {
	const details: ParsedOrderItem[] = [];

	const flexibleParseJson = (input: string): unknown => {
		const normalized = normalizeQuotes(input);
		try {
			return JSON.parse(normalized);
		} catch {
			try {
				return JSON.parse(normalized.replace(/'/g, '"'));
			} catch {
				return null;
			}
		}
	};

	const coerceQuantity = (input: unknown): number => {
		const numeric = Number(input);
		if (!Number.isFinite(numeric) || numeric <= 0) {
			return 1;
		}
		return Math.floor(numeric);
	};

	const coerceBoolean = (input: unknown, defaultValue = false): boolean => {
		if (typeof input === 'boolean') {
			return input;
		}
		if (typeof input === 'number') {
			return input !== 0;
		}
		if (typeof input === 'string') {
			const normalized = input.trim().toLowerCase();
			if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
				return true;
			}
			if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
				return false;
			}
		}
		return defaultValue;
	};

	const coerceVariantPrice = (input: unknown): number => {
		const numeric = Number(input);
		if (!Number.isFinite(numeric)) {
			return 0;
		}
		return numeric;
	};

	const normalizeVariantRecord = (entry: Record<string, unknown>): ParsedVariant | null => {
		const optionName = entry.optionName ?? entry.option ?? entry.name ?? entry.title;
		const variantName = entry.variantName ?? entry.value ?? entry.selection ?? entry.label ?? entry.choice ?? entry.name;
		if (typeof optionName !== 'string' || typeof variantName !== 'string') {
			return null;
		}
		return {
			optionName: optionName.trim(),
			variantName: variantName.trim(),
			variantPrice: coerceVariantPrice(entry.variantPrice ?? entry.price ?? entry.addonPrice ?? entry.addon_price ?? 0),
			isVariant: coerceBoolean(entry.isVariant ?? entry.is_variant ?? entry.variant, true),
			isMultiple: coerceBoolean(entry.isMultiple ?? entry.is_multiple ?? entry.multiple ?? entry.isMultipleSelection, false)
		};
	};

	const collectVariants = (input: unknown): ParsedVariant[] | undefined => {
		const variants: ParsedVariant[] = [];

		const visitVariant = (value: unknown) => {
			if (value === null || value === undefined) {
				return;
			}
			if (Array.isArray(value)) {
				value.forEach(visitVariant);
				return;
			}
			if (typeof value === 'string') {
				const trimmed = value.trim();
				if (!trimmed) {
					return;
				}
				const parsed = flexibleParseJson(trimmed);
				if (parsed && parsed !== trimmed) {
					visitVariant(parsed);
				}
				return;
			}
			if (typeof value === 'object') {
				const record = value as Record<string, unknown>;
				if ('optionName' in record || 'variantName' in record || 'value' in record || 'name' in record) {
					const normalized = normalizeVariantRecord(record);
					if (normalized) {
						variants.push(normalized);
					}
					return;
				}
				Object.values(record).forEach(visitVariant);
			}
		};

		visitVariant(input);
		if (!variants.length) {
			return undefined;
		}

		return variants
			.filter(variant => variant.optionName && variant.variantName)
			.sort((a, b) => {
				const optionCompare = a.optionName.localeCompare(b.optionName);
				if (optionCompare !== 0) {
					return optionCompare;
				}
				return a.variantName.localeCompare(b.variantName);
			});
	};

	const pushDetail = (id: unknown, quantity: unknown, variantsInput?: unknown) => {
		const normalizedId = typeof id === 'string' ? id.trim() : id != null ? String(id).trim() : '';
		if (!normalizedId) {
			return;
		}
		const normalizedVariants = collectVariants(variantsInput);
		const detail: ParsedOrderItem = {
			id: normalizedId,
			quantity: coerceQuantity(quantity)
		};
		if (normalizedVariants && normalizedVariants.length > 0) {
			detail.variants = normalizedVariants;
		}
		details.push(detail);
	};

	const visit = (entry: unknown) => {
		if (entry === null || entry === undefined) {
			return;
		}

		if (Array.isArray(entry)) {
			entry.forEach(visit);
			return;
		}

		if (typeof entry === 'string') {
			const trimmed = entry.trim();
			if (!trimmed) {
				return;
			}
			const parsed = flexibleParseJson(trimmed);
			if (parsed) {
				visit(parsed);
				return;
			}
			pushDetail(trimmed, 1);
			return;
		}

		if (typeof entry === 'object') {
			const record = entry as Record<string, unknown>;
			if ('id' in record) {
				pushDetail(record.id, record.quantity ?? 1, record.variants ?? record.variant ?? record.options);
				return;
			}
			// If object without id but has values, inspect its entries
			const values = Object.values(record);
			if (values.length > 0) {
				values.forEach(visit);
			}
			return;
		}
	};

	visit(value);
	return details;
};

const createVariantSignature = (variants?: ParsedVariant[]): string => {
	if (!variants || variants.length === 0) {
		return '';
	}
	return JSON.stringify(
		variants
			.map(variant => ({
				optionName: variant.optionName.trim().toLowerCase(),
				variantName: variant.variantName.trim().toLowerCase(),
				isVariant: variant.isVariant ? 1 : 0,
				isMultiple: variant.isMultiple ? 1 : 0,
				variantPrice: Number.isFinite(variant.variantPrice)
					? Number(variant.variantPrice).toFixed(4)
					: '0.0000'
			}))
			.sort((a, b) => {
				const optionCompare = a.optionName.localeCompare(b.optionName);
				if (optionCompare !== 0) {
					return optionCompare;
				}
				return a.variantName.localeCompare(b.variantName);
			})
	);
};

const createOrderItemKey = (item: ParsedOrderItem): string => {
	const baseId = item.id?.trim?.() ?? String(item.id ?? '').trim();
	const variantSignature = createVariantSignature(item.variants);
	return variantSignature ? `${baseId}::${variantSignature}` : baseId;
};

const mergeOrderItemDetails = (...lists: Array<ParsedOrderItem[]>): ParsedOrderItem[] => {
	const map = new Map<string, ParsedOrderItem>();
	for (const list of lists) {
		for (const item of list) {
			if (!item?.id) {
				continue;
			}
			const key = createOrderItemKey(item);
			const existing = map.get(key);
			if (existing) {
				existing.quantity += Number.isFinite(item.quantity) ? item.quantity : 1;
				if (!existing.variants && item.variants) {
					existing.variants = [...item.variants];
				}
			} else {
				map.set(key, {
					id: item.id,
					quantity: Number.isFinite(item.quantity) ? item.quantity : 1,
					variants: item.variants ? [...item.variants] : undefined
				});
			}
		}
	}
	return Array.from(map.values());
};

const coerceString = (value: unknown, fallback = ''): string => {
	if (typeof value === 'string') {
		const trimmed = value.trim();
		return trimmed.length > 0 ? trimmed : fallback;
	}
	if (value === null || value === undefined) {
		return fallback;
	}
	const stringified = String(value).trim();
	return stringified.length > 0 ? stringified : fallback;
};

const coerceNullableString = (value: unknown): string | null | undefined => {
	if (value === null) {
		return null;
	}
	if (value === undefined) {
		return undefined;
	}
	const normalized = coerceString(value);
	return normalized.length > 0 ? normalized : '';
};

const coerceBoolean = (value: unknown, defaultValue = false): boolean => {
	if (typeof value === 'boolean') {
		return value;
	}
	return defaultValue;
};

const coerceNumberFromCandidates = (
	defaultValue: number,
	...candidates: Array<unknown>
): number => {
	for (const candidate of candidates) {
		const numeric = Number(
			typeof candidate === 'string' ? candidate.trim() : candidate
		);
		if (Number.isFinite(numeric)) {
			return numeric;
		}
	}
	return defaultValue;
};

const sanitizeNumber = (value: unknown, fallback = 0): number => {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) {
		return fallback;
	}
	return numeric;
};

const sanitizeQuantity = (value: unknown, fallback = 0): number => {
	const numeric = sanitizeNumber(value, fallback);
	if (!Number.isFinite(numeric)) {
		return fallback;
	}
	return Math.max(0, Math.floor(numeric));
};

const sanitizeCartSelectionEntry = (
	selection: unknown,
	fallbackQuantity: number
): ChatbotCartSelection => {
	const record = (selection && typeof selection === 'object')
		? (selection as Record<string, unknown>)
		: {};
	const rawName = record.valueName ?? record.value_name ?? record.name ?? record.label ?? record.value;
	const valueName = coerceString(rawName, '');
	const rawId = record.valueId ?? record.value_id ?? record.id;
	const valueIdCandidate = coerceString(rawId ?? '', '');
	const valueId = valueIdCandidate ? valueIdCandidate : null;
	const priceDelta = coerceNumberFromCandidates(
		0,
		record.priceDelta,
		record.price_delta,
		record.price,
		record.delta,
		record.unitPrice,
		record.unit_price,
		record.addonPrice,
		record.addon_price,
		record.unitAddOnTotal,
		record.unit_add_on_total
	);
	const quantity = sanitizeQuantity(
		record.quantity ?? record.qty ?? record.count,
		Math.max(1, fallbackQuantity)
	);
	return {
		valueId,
		valueName,
		priceDelta,
		quantity
	};
};

const sanitizeCartOptionGroupEntry = (
	option: unknown,
	defaultQuantity: number,
	index: number
): ChatbotCartOptionGroup => {
	const record = (option && typeof option === 'object')
		? (option as Record<string, unknown>)
		: {};
	const rawGroupName =
		record.groupName ??
		record.group_name ??
		record.name ??
		record.title ??
		record.groupId ??
		record.group_id ??
		record.id;
	const groupName = coerceString(rawGroupName, '');
	const rawGroupId =
		record.groupId ??
		record.group_id ??
		record.id ??
		record.groupName ??
		record.group_name ??
		record.name;
	const fallbackId = groupName ? groupName.toLowerCase().replace(/\s+/g, '-') : `group-${index}`;
	const groupIdCandidate = coerceString(rawGroupId, '');
	const groupId = groupIdCandidate || fallbackId;
	const rawSelectionType =
		record.selectionType ??
		record.selection_type ??
		(record.isMultiple ? 'multiple' : undefined) ??
		(record.multiple ? 'multiple' : undefined);
	const selectionType = rawSelectionType === 'multiple' ? 'multiple' : 'single';
	const selectionsRaw = Array.isArray(record.selections)
		? record.selections
		: Array.isArray((record as { options?: unknown[] }).options)
			? (record as { options: unknown[] }).options
			: [];
	const selections = selectionsRaw
		.map(entry => sanitizeCartSelectionEntry(entry, defaultQuantity))
		.filter(entry => Boolean(entry.valueName));
	return {
		groupId,
		groupName,
		selectionType,
		selections
	};
};

const sanitizeCartItemEntry = (
	item: unknown,
	index: number
): ChatbotCartItem => {
	const record = (item && typeof item === 'object') ? (item as Record<string, unknown>) : {};
	const productIdCandidate =
		record.productId ??
		record.product_id ??
		record.itemId ??
		record.item_id ??
		record.id;
	const productId = coerceString(productIdCandidate, '');
	const quantity = sanitizeQuantity(
		record.quantity ?? record.qty ?? record.count ?? record.totalQuantity ?? record.total_quantity,
		0
	);
	const basePrice = coerceNumberFromCandidates(
		0,
		record.basePrice,
		record.base_price,
		record.unitBasePrice,
		record.unit_base_price,
		record.price,
		record.unitPrice,
		record.unit_price
	);
	const rawLineItemIdCandidate =
		record.lineItemId ??
		record.line_item_id ??
		record.lineId ??
		record.line_id ??
		(productId ? `${productId}__${index}` : null);
	const rawLineItemId = coerceString(rawLineItemIdCandidate ?? '', '');
	const lineItemId = rawLineItemId || (productId ? `${productId}__${index}` : `item-${index}`);
	const rawNotes =
		record.item_notes ??
		record.itemNotes ??
		record.specialInstructions ??
		record.notes ??
		record.note ??
		record.item_note;
	const itemNotes = coerceNullableString(rawNotes);
	const optionsRaw = Array.isArray(record.options)
		? record.options
		: Array.isArray((record as { optionGroups?: unknown[] }).optionGroups)
			? (record as { optionGroups: unknown[] }).optionGroups
			: Array.isArray((record as { option_groups?: unknown[] }).option_groups)
				? (record as { option_groups: unknown[] }).option_groups
				: [];
	const options = optionsRaw
		.map((option, optionIndex) => sanitizeCartOptionGroupEntry(option, quantity || 1, optionIndex))
		.filter(option => option.selections.length > 0);
	return {
		lineItemId,
		productId,
		quantity,
		basePrice,
		item_notes: itemNotes,
		options,
		isAiSuggestion: coerceBoolean(record.isAiSuggestion, false)
	};
};

const sanitizeCartSnapshotForRequest = (
	cart: ChatbotCartSnapshot | null | undefined
): ChatbotCartSnapshot => {
	const fallback: ChatbotCartSnapshot = {
		currencyId: '',
		currencyName: '',
		totalQuantity: 0,
		total: 0,
		order_notes: '',
		items: []
	};
	const source = cart ?? fallback;
	const currencyId = typeof source.currencyId === 'string' ? source.currencyId : '';
	const currencyName = typeof source.currencyName === 'string' ? source.currencyName : '';
	const totalQuantityInput = sanitizeQuantity(
		source.totalQuantity ?? (source as { total_quantity?: unknown }).total_quantity,
		0
	);
	const totalInput = sanitizeNumber(
		source.total ?? (source as { total_amount?: unknown }).total_amount ?? (source as { subtotal?: unknown }).subtotal,
		0
	);
	const orderNotesCandidate =
		source.order_notes !== undefined
			? source.order_notes
			: (source as unknown as { orderNotes?: unknown }).orderNotes ?? '';
	const orderNotes = orderNotesCandidate === null
		? null
		: typeof orderNotesCandidate === 'string'
			? orderNotesCandidate
			: String(orderNotesCandidate ?? '');
	const itemsRaw = Array.isArray(source.items) ? source.items : [];
	const items = itemsRaw
		.map((item, index) => sanitizeCartItemEntry(item, index))
		.filter(item => Boolean(item.productId));

	const totalQuantityFromItems = items.reduce((sum, item) => sum + (Number.isFinite(item.quantity) ? item.quantity : 0), 0);
	const totalQuantity = totalQuantityInput > 0 ? totalQuantityInput : totalQuantityFromItems;

	const computedItemsTotal = items.reduce((sum, item) => {
		const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
		const baseSubtotal = item.basePrice * quantity;
		const optionsSubtotal = item.options.reduce((groupSum, group) => {
			return (
				groupSum +
				group.selections.reduce((selectionSum, selection) => {
					const selectionQuantity = Number.isFinite(selection.quantity)
						? selection.quantity
						: Math.max(1, quantity);
					return selectionSum + selection.priceDelta * selectionQuantity;
				}, 0)
			);
		}, 0);
		return sum + baseSubtotal + optionsSubtotal;
	}, 0);
	const normalizedTotal = totalInput > 0 ? totalInput : computedItemsTotal;
	const total = Number.isFinite(normalizedTotal) ? Number.parseFloat(normalizedTotal.toFixed(2)) : 0;
	return {
		currencyId,
		currencyName,
		totalQuantity,
		total,
		order_notes: orderNotes,
		items
	};
};

const normalizeCartSnapshotFromResponse = (cart: unknown): ChatbotCartSnapshot | null => {
	if (!cart || typeof cart !== 'object') {
		return null;
	}
	const normalized = sanitizeCartSnapshotForRequest(cart as ChatbotCartSnapshot);
	return normalized;
};
/**
 * Parse streaming response from chatbot API
 */
export const sendMessageToStreamingApi = async (
	prompt: string,
	branch_id: string,
	session_id: string,
	cartSnapshot: ChatbotCartSnapshot,
	onChunk: (chunk: string) => void,
	onComplete: (result: StreamCompletionResult) => void,
	audio_base64?: string,
	content_type?: string,
	isRequestActive: () => boolean = () => true
): Promise<void> => {
	try {
		const normalizedContentType = normalizeContentTypeForApi(content_type);
		const sanitizedCart = sanitizeCartSnapshotForRequest(cartSnapshot);
		const payload: ChatbotApiRequest = {
			branch_id,
			session_id,
			audio_base64: audio_base64 ?? null,
			content_type: audio_base64 ? (normalizedContentType ?? 'wav') : null,
			prompt: audio_base64 ? null : (prompt ? prompt : null),
			cart: sanitizedCart
		};

		const response = await fetch(`${chatApiUrl}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
				'x-api-key' : "nneVddS5O4dMF_X36Q7vWse7DD1GWAUu6SkhG3pgalU"
			},
			body: JSON.stringify(payload)
		});

		if (!response.ok || !response.body) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';
		let rawContent = '';
		let sentCleanLength = 0;
		let latestCleanText = '';
		let parsedTags: Record<string, ParsedTagValue> = {};
		let streamCompleted = false;
		let audioData: { audioBase64: string; contentType: string } | null = null;

		try {
			while (true) {
				const { done, value } = await reader.read();
				if (done) {
					break;
				}

				buffer += decoder.decode(value, { stream: true });
				const lines = buffer.split('\n');
				buffer = lines.pop() ?? '';

				for (const rawLine of lines) {
					const line = rawLine.trim();
					if (!line || !line.startsWith('data: ')) {
						continue;
					}

					try {
						const jsonStr = line.slice(6);
						const data: StreamData = JSON.parse(jsonStr);
						if (data.type === 'content' && data.content) {
							rawContent += data.content;
							const extraction = extractTagsFromContent(rawContent);
							parsedTags = { ...parsedTags, ...extraction.tags };
							const safeText = removeTrailingPartialTag(extraction.text);
							latestCleanText = safeText;

							if (safeText.length > sentCleanLength && isRequestActive()) {
								const newChunk = safeText.slice(sentCleanLength);
								if (newChunk) {
									onChunk(newChunk);
								}
								sentCleanLength = safeText.length;
							}
						} else if (data.type === 'complete') {
							let finalRawContent = rawContent;
							if (data.full_content) {
								finalRawContent = data.full_content;
							}

							const extraction = extractTagsFromContent(finalRawContent);
							parsedTags = { ...parsedTags, ...extraction.tags };
							const finalText = removeTrailingPartialTag(extraction.text);

							if (finalText.length > sentCleanLength && isRequestActive()) {
								const newChunk = finalText.slice(sentCleanLength);
								if (newChunk) {
									onChunk(newChunk);
								}
							}
							sentCleanLength = finalText.length;
							latestCleanText = finalText;

							const payloadData = data.payload;
							const itemsFromTag = ensureStringArray(parsedTags['items_id']);
							const payloadItems = Array.isArray(payloadData?.ITEM_ID) ? payloadData.ITEM_ID : [];
							const baseItemsId = mergeUniqueStrings(payloadItems, itemsFromTag);

							const orderItemsFromTag = ensureOrderItemDetails(parsedTags['order_items']);
							const orderItemsDetails = mergeOrderItemDetails(orderItemsFromTag);

							const removedItemsFromTag = ensureOrderItemDetails(parsedTags['removed_items']);
							const removedItemsDetails = removedItemsFromTag;
							const itemsId = mergeUniqueStrings(baseItemsId, orderItemsDetails.map(item => item.id));

							// Check multiple sources for cart snapshot: data.updated_cart, data.payload.cart, and extracted cart tag
							const cartRaw = data.updated_cart ?? payloadData?.cart ?? parsedTags['cart'] ?? null;
							const cartFromPayload = normalizeCartSnapshotFromResponse(cartRaw);
							streamCompleted = true;
							
							// Store completion data but don't call onComplete yet - wait for audio event
							// Audio event comes AFTER complete event in the SSE stream
							parsedTags['__completionData'] = {
								text: finalText.trim(),
								tags: parsedTags,
								itemsId,
								orderItems: orderItemsDetails,
								removedItems: removedItemsDetails,
								cart: cartFromPayload
							} as unknown as ParsedTagValue;
							// Don't return here - continue processing to capture audio event
						}

						else if (data.type === 'audio' && data.audio) {
							// Store audio data
							audioData = {
								audioBase64: data.audio,
								contentType: data.content_type || 'audio/mpeg'
							};
							
							// If stream already completed, call onComplete now with audio
							if (streamCompleted && isRequestActive()) {
								const completionData = parsedTags['__completionData'] as unknown as {
									text: string;
									tags: Record<string, ParsedTagValue>;
									itemsId: string[];
											orderItems: ParsedOrderItem[];
											removedItems: ParsedOrderItem[];
									cart: ChatbotCartSnapshot | null;
								};
								if (completionData) {
									onComplete({
										...completionData,
										audioBase64: audioData.audioBase64,
										contentType: audioData.contentType
									});
									return;
								}
							}
						}
					} catch (parseError) {
						console.warn('Failed to parse streaming data:', parseError);
					}
				}
			}
		} finally {
			reader.releaseLock();
		}

		// Handle case where stream completed but onComplete wasn't called yet
		// This happens for text-only responses (no audio event)
		if (streamCompleted && isRequestActive()) {
			const completionData = parsedTags['__completionData'] as unknown as {
				text: string;
				tags: Record<string, ParsedTagValue>;
				itemsId: string[];
				orderItems: ParsedOrderItem[];
				removedItems: ParsedOrderItem[];
				cart: ChatbotCartSnapshot | null;
			};
			if (completionData) {
				onComplete({
					...completionData,
					...(audioData && {
						audioBase64: audioData.audioBase64,
						contentType: audioData.contentType
					})
				});
			}
		} else if (!streamCompleted && isRequestActive()) {
			const finalText = removeTrailingPartialTag(latestCleanText);
			const baseItemsId = ensureStringArray(parsedTags['items_id']);
			const orderItemsDetails = ensureOrderItemDetails(parsedTags['order_items']);
			const removedItemsDetails = ensureOrderItemDetails(parsedTags['removed_items']);
			const itemsId = mergeUniqueStrings(baseItemsId, orderItemsDetails.map(item => item.id));
			
		const cartFromTags = normalizeCartSnapshotFromResponse(parsedTags['cart']);
			
			onComplete({
				text: finalText.trim(),
				tags: parsedTags,
				itemsId,
				orderItems: orderItemsDetails,
				removedItems: removedItemsDetails,
				cart: cartFromTags,
				...(audioData && {
					audioBase64: audioData.audioBase64,
					contentType: audioData.contentType
				})
			});
		}
	} catch (error) {
		console.error('Error calling streaming chatbot API:', error);
		throw new Error('Failed to get streaming response from chatbot');
	}
};

/**
 * Parse XML response from chatbot API
 */
const parseXmlResponse = (xmlString: string): ParsedChatbotResponse => {
	// Simple XML parsing for the expected format
	const messageMatch = xmlString.match(/<message>(.*?)<\/message>/s);
	const itemsMatch = xmlString.match(/<suggested_items>(.*?)<\/suggested_items>/s);

	let suggested_items: string[] = [];
	if (itemsMatch) {
		const itemMatches = itemsMatch[1].match(/<item>(.*?)<\/item>/g);
		if (itemMatches) {
			suggested_items = itemMatches.map(item =>
				item.replace(/<item>|<\/item>/g, '').trim()
			);
		}
	}

	return {
		message: messageMatch ? messageMatch[1].trim() : '',
		suggested_items
	};
};

/**
 * Real API call to chatbot service
 */
export const sendMessageToApi = async (
	prompt: string,
	branch_id: string,
	session_id: string,
	cartSnapshot: ChatbotCartSnapshot
): Promise<ChatbotApiResponse> => {
	try {
	const payload: ChatbotApiRequest = {
		branch_id,
		session_id,
		audio_base64: null,
		content_type: null,
		prompt: prompt || null,
		cart: sanitizeCartSnapshotForRequest(cartSnapshot)
	};

		const apiURL = import.meta.env.VITE_CHATAPI_URL

		const response = await fetch(apiURL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`

			},
			body: JSON.stringify(payload)
		});
	
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const apiResponse: ChatbotApiResponse = await response.json();
	
		// Check if the response is successful based on the new format
		if (apiResponse.statusCode !== 200 || apiResponse.status !== "success") {
			return {
				statusCode: apiResponse.statusCode || 500,
				status: apiResponse.status || "error",
				time: apiResponse.time || 0,
				message: apiResponse.message || 'API response indicates failure',
				data: null
			};
		}

		return apiResponse
	} catch (error) {
		console.error('Error calling chatbot API:', error);
		throw new Error('Failed to get response from chatbot');
	}
};